import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./css/Perfil.css";
import { RootState } from "../../../redux/store/store";
import { useSelector } from "react-redux";
import { asignarAdmin } from "../../utils/asignarAdmin";
import { quitarAdmin } from "../../utils/quitarAdmin";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../redux/actions/loadingActions";
import { mostrarError, mostrarExito, mostrarInfo } from '../../utils/Alertas';
import { TextField } from "@mui/material";
import { clearUser } from "../../../redux/actions/authActions";
import { ROLES } from "../../utils/roles";

const Perfil: React.FC = () => {
    const dispatch = useDispatch();
	const navigate = useNavigate();
	const [nombre, setNombre] = useState("");
	const [email, setEmail] = useState("");
	const [fechaRegistro, setFechaRegistro] = useState<string | null>(null);
	const [nuevaPass, setNuevaPass] = useState("");
    const userRole = useSelector((state: RootState) => state.auth.userRole);
    const [nuevoAdminEmail, setNuevoAdminEmail] = useState("");
    const [currentPass, setCurrentPass] = useState("");
    const K_MAL_FORMATO_EMAIL_EN = 'The email address is improperly formatted.';
    const K_MAL_FORMATO_EMAIL_ES = 'La dirección de correo electrónico tiene un formato incorrecto';
    const A12_B = ROLES.A12_B;

    useEffect(() => {
        const fetchUserData = async () => {
            const user = auth.currentUser;

            // Si no hay usuario, redirigimos al Home
            if (!user) {
                navigate("/");
                return;
            }

            // Establece el email
            setEmail(user.email || "");

            // Recupera información del usuario desde Firestore
            const docRef = doc(db, "usuarios", user.uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setNombre(data.nombre);

                if (data.fechaRegistro?.toDate) {
                    const fecha = data.fechaRegistro.toDate();
                    setFechaRegistro(
                        fecha.toLocaleDateString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric"
                        })
                    );
                }
            }
        };

        fetchUserData();
        const root = document.getElementById("root");
        if (root) {
            root.scrollIntoView({ behavior: "auto" });
        }
    }, [navigate, dispatch]);

	const handleLogout = async () => {
        sessionStorage.setItem("logoutManual", "true");
        await signOut(auth);
        dispatch(clearUser());
        navigate("/");
    };

	const handlePasswordChange = async () => {
        // Validación mínima lado cliente
        if (nuevaPass.length < 6) {
            mostrarInfo("La nueva contraseña debe tener al menos 6 caracteres.");
            return;
        }
        if (currentPass.length === 0) {
            mostrarInfo("Introduce tu contraseña actual.");
            return;
        }

        try {
            dispatch(showLoading());

            const user = auth.currentUser;
            if (!user || !user.email) {
                throw new Error("No se encontró el usuario.");
            }

            const credential = EmailAuthProvider.credential(user.email, currentPass);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, nuevaPass);

            mostrarExito("Contraseña actualizada correctamente.");
            setCurrentPass("");
            setNuevaPass("");
        } catch (err: any) {
            switch (err.code) {
                case "auth/wrong-password":
                case "auth/invalid-credential":
                    mostrarError("Contraseña actual incorrecta.");
                    break;
                case "auth/too-many-requests":
                    mostrarError("Demasiados intentos. Intenta de nuevo más tarde.");
                    break;
                case "auth/requires-recent-login":
                    mostrarError("Tu sesión ha caducado. Vuelve a iniciar sesión.");
                    break;
                default:
                    mostrarError(`Error al cambiar la contraseña: ${err.message}`);
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleAsignarAdmin = async () => {
        if (!nuevoAdminEmail) {
            mostrarError("Email requerido");
            return;
        }

        try {
            dispatch(showLoading());
            await asignarAdmin(nuevoAdminEmail);
            mostrarExito(`${nuevoAdminEmail} ahora es administrador.`);
            setNuevoAdminEmail("");
        } catch (err: any) {
            if (err.message === K_MAL_FORMATO_EMAIL_EN) {
                mostrarError(K_MAL_FORMATO_EMAIL_ES);
            } else {
                mostrarError(err.message);
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleQuitarAdmin = async () => {
        if (!nuevoAdminEmail) {
            mostrarError("Email requerido");
            return;
        }

        try {
            dispatch(showLoading());
            await quitarAdmin(nuevoAdminEmail);
            mostrarExito(`Rol de admin eliminado a ${nuevoAdminEmail}.`);
            setNuevoAdminEmail("");
        } catch (err: any) {
            if (err.message === K_MAL_FORMATO_EMAIL_EN) {
                mostrarError(K_MAL_FORMATO_EMAIL_ES);
            } else {
                mostrarError(err.message);
            }
        } finally {
            dispatch(hideLoading());
        }
    };

	return (
        <div className="container-fluid">
            <div className="text-center">
                <h2>Mi Perfil</h2>
            </div>

            <div className="container-fluid">
                <div className="perfil-info">
                    <p><strong>Nombre:</strong> {nombre}</p>
                    <p><strong>Email:</strong> {email}</p>
                    {fechaRegistro && <p><strong>Fecha de registro:</strong> {fechaRegistro}</p>}
                </div>

                {userRole === A12_B && (
                    <div className="asignar-admin">
                        <div className="text-center">
                            <h4>Gestión de administradores</h4>
                        </div>

                        <TextField
                            type="email"
                            label="Correo del usuario"
                            variant="outlined"
                            value={nuevoAdminEmail}
                            onChange={(e) => setNuevoAdminEmail(e.target.value)}
                            fullWidth
                            margin="normal"
                        />

                        <div className="admin-buttons">
                            <button className="btn-quitar" onClick={handleQuitarAdmin}>Quitar admin</button>
                            <button className="btn-asignar" onClick={handleAsignarAdmin}>Asignar admin</button>
                        </div>
                    </div>
                )}

                <div className="perfil-password">
                    <div className="text-center">
                        <h4>Cambiar contraseña</h4>
                    </div>

                    <TextField
                        type="password"
                        label="Contraseña actual"
                        variant="outlined"
                        value={currentPass}
                        onChange={(e) => setCurrentPass(e.target.value)}
                        fullWidth
                        margin="normal"
                    />

                    <TextField
                        type="password"
                        label="Nueva contraseña (mín. 6)"
                        variant="outlined"
                        value={nuevaPass}
                        onChange={(e) => setNuevaPass(e.target.value)}
                        fullWidth
                        margin="normal"
                        style={{ marginTop: "0.5rem" }}
                    />

                    <button className="main-button" style={{ padding: "10px" }} onClick={handlePasswordChange}>Actualizar contraseña</button>
                </div>

                <button className="delete-primary-button" style={{ marginTop: "32px" }} onClick={handleLogout}>Cerrar sesión</button>
                <footer className="perfil-copy">© 2025 Morris y Luna</footer>
            </div>
		</div>
	);
};

export default Perfil;
